import { useEffect, useMemo, useRef, useState } from 'react';

type Phase = 'extraction' | 'analysis';

export default function LoadingVisual({ phase = 'extraction', minimal = false }: { phase?: Phase; minimal?: boolean }) {
  if (minimal) {
    return (
      <div className="mx-auto h-10 w-10" aria-label="Loading">
        <svg viewBox="0 0 50 50" className="h-10 w-10">
          <circle cx="25" cy="25" r="21" stroke="#e5e7eb" strokeWidth="4" fill="none" />
          <g className="animate-spin origin-center" style={{ color: '#64748b' }}>
            <circle
              cx="25"
              cy="25"
              r="21"
              stroke="currentColor"
              strokeWidth="4"
              strokeLinecap="round"
              strokeDasharray="60 120"
              fill="none"
            />
          </g>
        </svg>
      </div>
    );
  }

  const cfg = useMemo(() => {
    if (phase === 'analysis') {
      return {
        typicalSeconds: 240,
        typicalCopy: 'Our AI is working its magic. This usually takes 2 to 4 minutes.',
        steps: [
          'Starting',
          'Validating input data',
          'Aggregating menu data',
          'Normalizing extracted fields',
          'Benchmarking vs. similar venues',
          'Segmenting by product type and category',
          'Identifying pricing patterns',
          'Modeling item performance potential',
          'Forecasting out-the-door totals',
          'Simulating customer purchase behavior',
          'Detecting pricing inefficiencies',
          'Generating optimization strategies',
          'Estimating revenue and margin impact',
          'Evaluating risk and sensitivity',
          'Prioritizing recommendations by ROI',
          'Adjusting for regional and seasonal factors',
          'Visualizing comparative benchmarks',
          'Summarizing actionable insights',
          'Finalizing recommendation report',
          'Wrapping up'
        ]
      } as const;
    }
    return {
      typicalSeconds: 120,
      typicalCopy: 'Our AI is working its magic. This usually takes 1 to 2 minutes.',
      steps: [
        'Initializing',
        'Setting up your menu scan',
        'Reading your menu',
        'Analyzing structure',
        'Scanning pages',
        'Extracting event details',
        'Extracting products',
        'Identifying pricing',
        'Detecting categories',
        'Identifying tax and fees',
        'Organizing extracted data',
        'Finalizing details',
        'Almost done'
      ]
    } as const;
  }, [phase]);

  const [index, setIndex] = useState(1);
  const [elapsed, setElapsed] = useState(0);
  const [dotTick, setDotTick] = useState(0);
  const timerRef = useRef<number | null>(null);
  const tickRef = useRef<number | null>(null);
  const dotRef = useRef<number | null>(null);

  useEffect(() => {
    setIndex(0);
    setElapsed(0);
    setDotTick(0);
  }, [phase, cfg.steps.length]);

  useEffect(() => {
    if (timerRef.current) window.clearInterval(timerRef.current);
    // Advance one step per segment; do not loop after the last step
    const segmentMs = Math.max(1500, Math.floor((cfg.typicalSeconds * 1000) / Math.max(1, cfg.steps.length)));
    timerRef.current = window.setInterval(() => {
      setIndex((i) => {
        // stop at the last REAL step (exclude sentinel at the end)
        const lastReachable = Math.max(0, cfg.steps.length - 1);
        if (i >= lastReachable) {
          // stop at the last step
          if (timerRef.current) window.clearInterval(timerRef.current);
          return i;
        }
        return i + 1;
      });
    }, segmentMs);
    return () => {
      if (timerRef.current) window.clearInterval(timerRef.current);
    };
  }, [cfg.steps.length, cfg.typicalSeconds]);

  // Elapsed seconds for progress bar
  useEffect(() => {
    if (tickRef.current) window.clearInterval(tickRef.current);
    tickRef.current = window.setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => { if (tickRef.current) window.clearInterval(tickRef.current); };
  }, [phase]);

  // Animate trailing dots for the active step
  useEffect(() => {
    if (dotRef.current) window.clearInterval(dotRef.current);
    dotRef.current = window.setInterval(() => setDotTick((d) => (d + 1) % 8), 500);
    return () => { if (dotRef.current) window.clearInterval(dotRef.current); };
  }, [index, phase]);

  return (
    <div className="w-full" aria-live="polite" role="status">
      {/* progress bar */}
      <div className="w-full overflow-hidden rounded-full bg-slate-200">
        {(() => {
          const hardMax = 99;
          const base = 5;
          const targetMax = 98;
          const ratio = Math.max(0, Math.min(1, elapsed / cfg.typicalSeconds));
          const progress = Math.min(hardMax, Math.floor(base + (targetMax - base) * ratio));
          return (
            <div
              className="h-2 rounded-full transition-all duration-700"
              style={{ width: `${progress}%`, backgroundColor: 'var(--color-primary)' }}
            />
          );
        })()}
      </div>
      {/* Scan visual */}
      <div className="mt-6 relative mx-auto w-full max-w-md">
        <div className="relative w-full aspect-[4/3] rounded-2xl border border-slate-200 bg-white/70 shadow-xl overflow-hidden">
          {/* Background skeleton rows */}
          <div className="absolute inset-0 p-6 opacity-25">
            <div className="space-y-3">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="flex items-center justify-between">
                  <div className="h-2 rounded bg-slate-300/90" style={{ width: i % 3 === 0 ? '65%' : '52%' }} />
                  <div className="h-2 w-12 rounded bg-slate-300/90" />
                </div>
              ))}
            </div>
          </div>

          {/* Scan line */}
          <div className="absolute inset-0 overflow-hidden">
            <div
              className="scan-line absolute left-0 right-0 h-1 opacity-90"
              style={{
                background:
                  'linear-gradient(90deg, transparent, var(--color-primary), transparent)',
                boxShadow:
                  '0 0 18px color-mix(in oklch, var(--color-primary) 55%, transparent)'
              }}
            />
          </div>

          {/* Center glow */}
          <div className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
            <div
              style={{
                width: 160,
                height: 160,
                borderRadius: '9999px',
                background:
                  'radial-gradient(circle, color-mix(in oklch, var(--color-primary) 30%, transparent) 0%, transparent 70%)',
                filter: 'blur(24px)',
                opacity: 0.55
              }}
            />
          </div>

          {/* Corner indicators */}
          <div className="absolute top-3 left-3 w-6 h-6 border-t-2 border-l-2" style={{ borderColor: 'color-mix(in oklch, var(--color-primary) 65%, white)' }} />
          <div className="absolute top-3 right-3 w-6 h-6 border-t-2 border-r-2" style={{ borderColor: 'color-mix(in oklch, var(--color-primary) 65%, white)' }} />
          <div className="absolute bottom-3 left-3 w-6 h-6 border-b-2 border-l-2" style={{ borderColor: 'color-mix(in oklch, var(--color-primary) 65%, white)' }} />
          <div className="absolute bottom-3 right-3 w-6 h-6 border-b-2 border-r-2" style={{ borderColor: 'color-mix(in oklch, var(--color-primary) 65%, white)' }} />

          {/* Particles */}
          <div className="absolute inset-0 pointer-events-none">
            <span className="particle absolute top-1/4 left-1/4 h-2 w-2 rounded-full" style={{ backgroundColor: 'var(--color-primary)', opacity: 0.6 }} />
            <span className="particle absolute top-1/3 right-1/3 h-2 w-2 rounded-full" style={{ backgroundColor: 'color-mix(in oklch, var(--color-primary) 60%, #7c3aed)', opacity: 0.6, animationDelay: '0.4s' }} />
            <span className="particle absolute bottom-1/4 left-1/3 h-2 w-2 rounded-full" style={{ backgroundColor: 'var(--color-primary)', opacity: 0.6, animationDelay: '0.8s' }} />
            <span className="particle absolute bottom-1/3 right-1/4 h-2 w-2 rounded-full" style={{ backgroundColor: 'color-mix(in oklch, var(--color-primary) 60%, #7c3aed)', opacity: 0.6, animationDelay: '1.2s' }} />
          </div>
        </div>

        {/* Active step text + dots */}
        <div className="mt-8 mb-4 py-2 text-center">
          <span key={index} className="shimmer-wrap text-base font-semibold labs-step-rotate" style={{ color: 'var(--color-primary)' }}>
            <span className="shimmer-base">
              {cfg.steps[index]}...
            </span>
            <span className="shimmer-overlay" aria-hidden>
              {cfg.steps[index]}...
            </span>
          </span>
          <div className="mt-8 flex items-center justify-center gap-2">
            {[0, 1, 2].map((i) => (
              <span
                // eslint-disable-next-line react/no-array-index-key
                key={i}
                className="h-2 w-2 rounded-full bg-[color:var(--color-primary)] animate-bounce"
                style={{ animationDelay: `${i * 0.15}s` }}
              />
            ))}
          </div>
        </div>
      </div>
      {/* Typical copy now rendered by page heading area */}
    </div>
  );
}
